import { Component } from '@angular/core';
import { AppService } from '../../../../app.service';
import { EtlIngestionService } from '../../../../services/etl-ingestion/etl-ingestion.service';
import { Creditor } from '../../../../interfaces/creditor.interface';
import moment from 'moment';
import { ReportsService } from '../../../../services/reports/reports.service';
import { finalize, tap } from 'rxjs';
import { getBatch, isArrayWithData } from '../../../../utilities/array.helpers';
import { EChartsOption, graphic } from 'echarts';
import { getGraphHeight } from '../../../dashboard/helpers/data-helper';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { ChartColorUtils } from '../../../../utilities/color.helpers';

@Component({
    selector: 'app-payment-plan-forecast',
    templateUrl: './payment-plan-forecast.component.html',
    styleUrl: './payment-plan-forecast.component.css',
})
export class PaymentPlanForecastComponent {
    clients: Creditor[] = [];
    selectedClients: Creditor[] = [];
    tableData: any[] = [];
    tableDetails: any[] = [];
    isOpenDetails: boolean = false;

    chartOptions: EChartsOption[] = [];
    startDate: Date = new Date();
    monthIndexes = Array.from({ length: 12 }, (_, i) => i);

    getGraphHeight = getGraphHeight;
    isArrayWithData = isArrayWithData;

    public constructor(
        public appService: AppService,
        private etlIngestionService: EtlIngestionService,
        private reportsService: ReportsService,
    ) {}

    public ngOnInit(): void {
        this.getCreditors();
    }

    monthFromStartDate(index: number): string {
        return moment(this.startDate).add(index, 'months').format('MMM YYYY');
    }

    getCreditors() {
        this.etlIngestionService.getCreditors().subscribe((res: any) => {
            if (
                !!res &&
                !!res.Creditors &&
                Array.isArray(res.Creditors) &&
                res.Creditors.length > 0
            ) {
                this.clients = res.Creditors;
            }
        });
    }

    generateGraphCreditorCounts() {
        this.chartOptions = [];

        ChartColorUtils.reset();

        const totalPerCreditorAll = this.tableData.map(
            (i: any) => i.totalPerCreditor,
        );

        const creditors = this.tableData.map((i: any) => i.creditorId);

        const globalMax = Math.max(...totalPerCreditorAll);

        const TARGET_AXIS_MAX = 160_000;

        let index = 0;

        while (true) {
            const creditorBatch = getBatch(creditors, index);

            if (!isArrayWithData(creditorBatch)) break;

            const totalPerCreditorBatch = getBatch(totalPerCreditorAll, index);

            const decayFactor = Math.pow(0.75, index);
            const visualMax = globalMax / decayFactor;

            const axisDivisor =
                visualMax > TARGET_AXIS_MAX ? visualMax / TARGET_AXIS_MAX : 1;

            const isLastBatch = !isArrayWithData(
                getBatch(creditors, index + 1),
            );

            this.chartOptions.push({
                dataLength: creditorBatch.length,

                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                },

                grid: {
                    left: 130,
                    right: 120,
                    top: index === 0 ? 20 : 0,
                    bottom: 0,
                },

                xAxis: {
                    type: 'value',
                    min: 0,
                    max: visualMax,

                    axisLabel: {
                        show: isLastBatch,
                        formatter: (v: number) =>
                            Math.round(v / axisDivisor).toLocaleString(),
                    },

                    axisTick: { show: isLastBatch },
                    axisLine: { show: isLastBatch },
                    splitLine: { show: false },
                },

                yAxis: {
                    type: 'category',
                    data: creditorBatch,
                    inverse: true,
                    axisLine: { show: true },
                    axisTick: { show: false },
                },

                series: [
                    {
                        type: 'bar',
                        barWidth: 26,

                        label: {
                            show: true,
                            position: 'right',
                            distance: 2,
                            fontWeight: 700,
                            color: '#1f2933',

                            formatter: (p: CallbackDataParams) =>
                                totalPerCreditorBatch[
                                    p.dataIndex!
                                ]?.toLocaleString(),
                        },

                        data: totalPerCreditorBatch.map(
                            (value: number, i: number) => {
                                const creditorId = creditorBatch[i];

                                return {
                                    value,

                                    itemStyle: {
                                        color: ChartColorUtils.getColor(
                                            creditorId,
                                        ),
                                    },
                                };
                            },
                        ),
                    },
                ],
            });

            index++;
        }
    }

    openDetails(creditorId: string) {
        this.appService.isShowPageSpinner = true;
        this.reportsService
            .getDataPaymentPlanForeCastDetail(
                moment(this.startDate).format('YYYY-MM-DD'),
                creditorId.replace('creditor/', ''),
            )
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.isOpenDetails = true;
                        this.tableDetails = res;
                    }
                }),
                finalize(() => (this.appService.isShowPageSpinner = false)),
            )
            .subscribe();
    }

    getData() {
        this.appService.isShowPageSpinner = true;
        this.reportsService
            .getDataPaymentPlanForeCast(
                moment(this.startDate).format('YYYY-MM-DD'),
                this.selectedClients.map((i: Creditor) => i.CreditorId),
            )
            .pipe(
                tap((res: any) => {
                    if (isArrayWithData(res)) {
                        this.tableData = res;
                        this.generateGraphCreditorCounts();
                    }
                }),

                finalize(() => (this.appService.isShowPageSpinner = false)),
            )
            .subscribe();
    }

    mockData() {
        this.tableData = [
            {
                creditorId: 'creditor/214',
                creditorName: 'Test Creditor',
                totalPerCreditor: 300,
                totalCountPerCreditor: 10,
                month1: 100,
                month2: 200,
                month3: 300,
                month4: 400,
                month5: 500,
                month6: 600,
                month7: 700,
                month8: 800,
                month9: 900,
                month10: 1000,
                month11: 1100,
                month12: 1200,
            },
        ];
    }
}
